import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "../ui/sonner";
import { Building2, FileText, Loader2, Save } from "lucide-react";

const GC_KEYS = [["company", "公司名称", "Company"], ["address", "地址", "Address"], ["license", "CSLB 执照号", "CSLB license"], ["phone", "电话", "Phone"], ["email", "邮箱", "Email"]];

/* 甲方（GC）合同信息 + 按工种 docx 合同模板映射（管理员 / 工程经理）。 */
export const ContractTemplateSettings = () => {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [gc, setGc] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/gc-profile").then((r) => setGc(r.data)).catch(() => {});
    api.get("/settings/subcontract-docx-templates").then((r) => setTpl(r.data)).catch(() => {});
  }, [api]);
  const saveGc = async () => {
    setBusy(true);
    try { const { data } = await api.put("/settings/gc-profile", gc); setGc((g) => ({ ...g, ...data })); toast.success(zh ? "甲方信息已保存，之后生成的合同即生效" : "GC profile saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const setTrade = async (trade, key) => {
    try { const { data } = await api.put("/settings/subcontract-docx-templates", { byTrade: { ...tpl.byTrade, [trade]: key || null } }); setTpl((t) => ({ ...t, byTrade: data.byTrade })); toast.success(zh ? "模板映射已更新" : "Template mapping updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  if (!gc || !tpl) return null;
  const trades = [...new Set(tpl.templates.flatMap((t) => t.trades))];
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="contract-template-settings">
      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand-500" />{zh ? "甲方（GC）合同信息" : "General Contractor profile"}</h3>
        <p className="text-[11px] text-slate-500">{zh ? "打印在 docx 分包合同的抬头、甲方栏、签名栏与页脚；保存后新生成的合同 PDF 立即使用。" : "Printed in the header, GC block, signature block and footer of docx subcontracts."}</p>
        {GC_KEYS.map(([k, z, e]) => (
          <label key={k} className="block text-[11px] text-slate-600">{zh ? z : e}
            <Input data-testid={`gc-${k}`} value={gc[k] || ""} onChange={(ev) => setGc((g) => ({ ...g, [k]: ev.target.value }))} disabled={!gc.canEdit} className="h-8 text-xs mt-0.5" />
          </label>
        ))}
        {gc.canEdit && <Button onClick={saveGc} disabled={busy} className="bg-brand-500 hover:bg-brand-600 h-8 text-xs" data-testid="gc-save">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}</Button>}
      </section>
      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><FileText className="h-4 w-4 text-brand-500" />{zh ? "分包合同模板（按工种）" : "Subcontract templates by trade"}</h3>
        <p className="text-[11px] text-slate-500">{zh ? "选择 docx 模板的工种，其「合同 PDF / 合同包 / 电子签 / 签署链接」统一按模板渲染并支持「合同字段」自动拾取；选「系统双语 PDF」回退原格式。" : "Trades mapped to a docx template render PDF / package / e-sign / signing link from it, with auto-filled contract fields."}</p>
        {trades.map((trade) => (
          <div key={trade} className="flex items-center gap-2 text-xs" data-testid={`tpl-row-${trade}`}>
            <span className="w-24 font-medium text-slate-700 capitalize">{trade}</span>
            <select data-testid={`tpl-select-${trade}`} value={tpl.byTrade[trade] || ""} disabled={!tpl.canEdit} onChange={(e) => setTrade(trade, e.target.value)} className="h-8 rounded-md border border-slate-200 px-2 text-xs flex-1 bg-white">
              <option value="">{zh ? "系统双语 PDF（原格式）" : "Built-in bilingual PDF"}</option>
              {tpl.templates.filter((t) => t.trades.includes(trade)).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>
        ))}
      </section>
    </div>
  );
};
